import { Button, IconSet } from '@postscript/components';
import { FB_BASIC_CAMPAIGNS } from 'components/admin/utils/feature-flags';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import CtaBox from 'components/flowBuilder/components/CtaBox';
import { FlowWizardOptions } from 'components/flowBuilder/constants/trackingEvents';
import {
  FlowType,
  FlowWizardOptionsType,
  SubmitCreateFlowArgs,
} from 'components/flowBuilder/types';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useEntitlements } from 'providers/entitlements/EntitlementsProvider';
import { EntitlementKeys } from 'providers/entitlements/types';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  COMMON_EVENT_NAMES,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';
import { parseFlowType } from '../utils/flows';

const StyledGridWrapper = styled.div<{
  itemCount?: number;
  variant?: 'modal' | 'page';
}>`
  display: grid;
  justify-content: ${({ variant }) =>
    variant === 'modal' ? 'center' : 'start'};
  gap: var(--spacing-4);
  grid-template-columns: ${({ itemCount, variant }) =>
    `repeat(${variant === 'modal' ? itemCount : 'auto-fit'}, 288px)`};
`;

const StyledCtaBoxWrapper = styled.div<{ order?: number }>`
  ${({ order }) => order !== undefined && `order: ${order};`}
  div:first-child {
    background-color: var(--surface-bkg-color);
  }
`;

const StyledButton = styled(Button)`
  grid-column: span 2;
  margin-top: var(--spacing-3);
  margin-left: auto;
  margin-right: auto;
`;

interface CreateFlowGridProps {
  variant?: 'modal' | 'page';
  flowType: FlowType;
  handleSubmit: (args?: SubmitCreateFlowArgs) => void;
  onChooseTemplate: () => void;
}

const CAMPAIGN_ORDER_CONFIG = {
  [FlowWizardOptions.BASIC_CAMPAIGN]: 1,
  [FlowWizardOptions.CAMPAIGN_FLOW]: 2,
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN]: 4,
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES]: 4,
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE]: 4,
  [FlowWizardOptions.CAMPAIGN_TEMPLATE]: 5,
  [FlowWizardOptions.LEGACY_CAMPAIGN]: 3,
} as const;

const AUTOMATION_ORDER_CONFIG = {
  [FlowWizardOptions.AUTOMATION]: 1,
  [FlowWizardOptions.AUTOMATION_TEMPLATE]: 2,
} as const;

const infinityBoxContent = {
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN]: {
    from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN,
    label: 'Create an Infinity Test Campaign',
    tag: undefined,
  },
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES]: {
    from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES,
    label: 'Contact Sales',
    tag: undefined,
  },
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE]: {
    from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE,
    label: 'Learn More',
    tag: {
      text: 'Trial Available',
      variant: 'success',
    },
  },
} as const;

export default function CreateFlowGrid({
  flowType,
  handleSubmit,
  onChooseTemplate,
  variant = 'page',
}: CreateFlowGridProps) {
  const { isAutomation, isCampaign } = parseFlowType(flowType);
  const { hasLabsFlag } = usePSLabs();
  const hasBasicCampaignsFlag = hasLabsFlag(FB_BASIC_CAMPAIGNS);

  const modalType = isCampaign
    ? MODAL_TYPES.CREATE_CAMPAIGN
    : MODAL_TYPES.CREATE_AUTOMATION;

  // Campaign-specific infinity test logic
  const billingPermissions = useInfinityTestingBillingStatus();
  const { isEligible, hasProduct } = billingPermissions;
  const infinityTestOption = isCampaign
    ? hasProduct
      ? FlowWizardOptions.INFINITY_TEST_CAMPAIGN
      : isEligible
      ? FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE
      : undefined
    : undefined;

  const { isEntitledTo } = useEntitlements();
  const hasCampaignsTabsViewEntitlement =
    isEntitledTo(EntitlementKeys.FB_CAMPAIGNS_TABS_VIEW) ||
    isEntitledTo(EntitlementKeys.FB_CAMPAIGNS_TABS_VIEW_DEPRECATE);

  // True for BC feature flag users, OR custom cart users who can't see legacy campaigns
  const showBasicCampaignOption =
    hasBasicCampaignsFlag || !hasCampaignsTabsViewEntitlement;
  // False for custom cart users who can't see legacy campaigns
  const showLegacyCampaignOption =
    !hasBasicCampaignsFlag && hasCampaignsTabsViewEntitlement;
  const showInfinityTestOption = infinityTestOption != null;
  const showCampaignTemplateOption =
    isEntitledTo(EntitlementKeys.FB_TEMPLATES_CAMPAIGN_EXTENDED) ||
    isEntitledTo(EntitlementKeys.FB_TEMPLATES_CAMPAIGN_EXTENDED_DEPRECATE);

  const handleButtonClick = useCallback(
    (type: FlowWizardOptionsType) => {
      // Log the event
      logEvent(COMMON_EVENT_NAMES.MODAL_CTA_CLICKED, {
        flow_type: flowType,
        modal_type: modalType,
        modal_selection_value: type,
        product_area: PRODUCT_AREAS.FLOW_BUILDER,
      });

      // Handle template selection
      if (
        type === FlowWizardOptions.CAMPAIGN_TEMPLATE ||
        type === FlowWizardOptions.AUTOMATION_TEMPLATE
      ) {
        onChooseTemplate();
        return;
      }

      // Submit for all other options
      handleSubmit({ from: type });
    },
    [flowType, modalType, onChooseTemplate, handleSubmit],
  );

  const itemCount = useMemo(() => {
    if (isAutomation) return 2;
    if (variant === 'modal' && showInfinityTestOption) return 2;
    return 3;
  }, [isAutomation, variant, showInfinityTestOption]);

  // Campaign-specific options
  if (isCampaign) {
    return (
      <StyledGridWrapper variant={variant} itemCount={itemCount}>
        {showBasicCampaignOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.BASIC_CAMPAIGN]}
          >
            <CtaBox
              cta={{
                dataCy: 'create-basic-campaign-button',
                label: 'Create a Basic Campaign',
                handleClick: () =>
                  handleButtonClick(FlowWizardOptions.BASIC_CAMPAIGN),
              }}
              description="Send a single-message campaign to a segment."
              icon={IconSet.Announcement}
              title="Basic Campaign"
            />
          </StyledCtaBoxWrapper>
        )}
        {showInfinityTestOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[infinityTestOption]}
          >
            <CtaBox
              cta={{
                dataCy: 'create-infinity-test-campaign-button',
                label: infinityBoxContent[infinityTestOption].label,
                handleClick: () =>
                  handleButtonClick(
                    infinityBoxContent[infinityTestOption].from,
                  ),
              }}
              description="Continuously test AI message variants to maximize EPM."
              icon={IconSet.Sparkles}
              tag={infinityBoxContent[infinityTestOption].tag}
              title="AI Infinity Test Campaign"
            />
          </StyledCtaBoxWrapper>
        )}
        <StyledCtaBoxWrapper
          order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.CAMPAIGN_FLOW]}
        >
          <CtaBox
            cta={{
              dataCy: 'create-flow-campaign-button',
              label: 'Create a Campaign Flow',
              handleClick: () =>
                handleButtonClick(FlowWizardOptions.CAMPAIGN_FLOW),
            }}
            description="Build a campaign flow from a blank canvas."
            icon={IconSet.Split}
            title="Create a Campaign Flow"
          />
        </StyledCtaBoxWrapper>
        {showCampaignTemplateOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.CAMPAIGN_TEMPLATE]}
          >
            <CtaBox
              cta={{
                dataCy: 'choose-template-button',
                label: 'Choose a Template',
                handleClick: () =>
                  handleButtonClick(FlowWizardOptions.CAMPAIGN_TEMPLATE),
              }}
              description="Need some inspiration? Start with a prebuilt campaign."
              icon={IconSet.GridMasonry}
              title="Campaign Templates"
            />
          </StyledCtaBoxWrapper>
        )}
        {showLegacyCampaignOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.LEGACY_CAMPAIGN]}
          >
            <CtaBox
              cta={{
                dataCy: 'create-legacy-flow-campaign-button',
                label: 'Create a Standard Campaign',
                handleClick: () =>
                  handleButtonClick(FlowWizardOptions.LEGACY_CAMPAIGN),
              }}
              description="Send a single-message campaign to a segment."
              icon={IconSet.Announcement}
              title="Standard Campaign"
            />
          </StyledCtaBoxWrapper>
        )}
      </StyledGridWrapper>
    );
  }

  // Automation-specific options
  return (
    <div>
      <StyledGridWrapper variant={variant} itemCount={itemCount}>
        <StyledCtaBoxWrapper
          order={AUTOMATION_ORDER_CONFIG[FlowWizardOptions.AUTOMATION]}
        >
          <CtaBox
            cta={{
              dataCy: 'create-flow-automation-button',
              label: 'Create an Automation Flow',
              handleClick: () =>
                handleButtonClick(FlowWizardOptions.AUTOMATION),
              variation: 'primary',
            }}
            description="Build an automation flow from a blank canvas."
            icon={IconSet.Split}
            title="Automation Flow"
          />
        </StyledCtaBoxWrapper>
        <StyledCtaBoxWrapper
          order={AUTOMATION_ORDER_CONFIG[FlowWizardOptions.AUTOMATION_TEMPLATE]}
        >
          <CtaBox
            cta={{
              dataCy: 'choose-automation-template-button',
              label: 'Choose a Template',
              handleClick: () =>
                handleButtonClick(FlowWizardOptions.AUTOMATION_TEMPLATE),
            }}
            description="Need some inspiration? Start with a prebuilt automation."
            icon={IconSet.GridMasonry}
            title="Automation Templates"
          />
        </StyledCtaBoxWrapper>
      </StyledGridWrapper>
      <StyledGridWrapper itemCount={itemCount} variant={variant}>
        <StyledButton
          variant="plain"
          onClick={() => handleButtonClick(FlowWizardOptions.LEGACY_AUTOMATION)}
        >
          Create a Legacy Automation
        </StyledButton>
      </StyledGridWrapper>
    </div>
  );
}
